import { ChangeDetectionStrategy, Component, computed, inject, signal, OnDestroy, viewChild, ElementRef } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { OverlayModule, type ConnectedPosition } from '@angular/cdk/overlay';
import { TranslatePipe } from '@ngx-translate/core';
import { ZagIcon } from '@zag/ui';
import { type NotificationResponse } from '@zag/notifications/common';
import { NotificationsService } from '../../services/notifications.service';
import { NotificationItemComponent } from '../notification-item/notification-item.component';
import { type NotificationCreatedEvent } from '../../gateways/notification-gateway.client';

/** Map a real-time WebSocket event to the shared NotificationResponse shape. */
function toNotificationResponse(event: NotificationCreatedEvent): NotificationResponse {
  return {
    id: event.id,
    eventId: event.id,
    recipientId: '',
    title: event.title,
    message: event.message,
    type: event.type,
    priority: event.priority,
    category: event.category,
    metadata: event.metadata,
    read: event.read ?? false,
    readAt: event.readAt ?? null,
    archived: false,
    expiresAt: null,
    createdAt: event.createdAt,
  };
}

/**
 * Notification bell icon with unread badge for the top bar.
 * Click to open an inline dropdown panel of the latest notifications.
 * Designed to be placed in the `[slot=actions]` slot of `<zag-top-bar>`.
 */
@Component({
  selector: 'zag-notification-bell',
  imports: [TranslatePipe, ZagIcon, OverlayModule, RouterModule, NotificationItemComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="notification-bell" cdkOverlayOrigin #bellOrigin="cdkOverlayOrigin">
      <!-- Bell button -->
      <button
        #bellButton
        type="button"
        class="top-bar__action-button notification-bell__button"
        [class.notification-bell__button--active]="panelOpen()"
        [attr.aria-label]="bellAriaLabel()"
        [attr.aria-expanded]="panelOpen()"
        [attr.aria-haspopup]="true"
        (click)="togglePanel()"
      >
        <span zagIcon="notifications" class="notification-bell__icon"></span>
        @if (unreadCount() > 0) {
          <span class="notification-bell__badge" [attr.aria-hidden]="true">
            {{ unreadCount() > 99 ? '99+' : unreadCount() }}
          </span>
        }
      </button>
    </div>

    <!-- Panel overlay -->
    <ng-template
      cdkConnectedOverlay
      [cdkConnectedOverlayOrigin]="bellOrigin"
      [cdkConnectedOverlayOpen]="panelOpen()"
      [cdkConnectedOverlayHasBackdrop]="true"
      [cdkConnectedOverlayBackdropClass]="'notification-panel-backdrop'"
      [cdkConnectedOverlayPositions]="panelPositions"
      [cdkConnectedOverlayWidth]="360"
      [cdkConnectedOverlayPanelClass]="'notification-panel-overlay'"
      (backdropClick)="closePanel()"
      (detach)="closePanel()"
    >
      <div class="notification-panel" role="dialog" [attr.aria-label]="'notifications.title' | translate">
        <!-- Panel header -->
        <div class="notification-panel__header">
          <h2 class="notification-panel__title">{{ 'notifications.title' | translate }}</h2>
          @if (unreadCount() > 0) {
            <span class="notification-panel__count">
              {{ 'notifications.unreadCount' | translate: { count: unreadCount() } }}
            </span>
          }
          @if (hasUnread()) {
            <button
              type="button"
              class="notification-panel__mark-all-btn"
              [attr.aria-label]="'notifications.markAllAsRead' | translate"
              [attr.title]="'notifications.markAllAsRead' | translate"
              (click)="onMarkAllAsRead()"
            >
              <span zagIcon="done_all"></span>
            </button>
          }
        </div>

        <!-- Notification list -->
        <div class="notification-panel__list" role="list">
          @if (latestNotifications().length === 0) {
            <div class="notification-panel__empty">
              <span zagIcon="notifications_off" class="notification-panel__empty-icon"></span>
              <p class="notification-panel__empty-text">{{ 'notifications.empty' | translate }}</p>
            </div>
          } @else {
            @for (notification of latestNotifications(); track notification.id) {
              <a class="notification-panel__item-link" href="#" (click)="onNotificationClick(notification, $event)">
                <zag-notification-item
                  [notification]="notification"
                  [compact]="true"
                  [showActions]="false"
                  (markAsRead)="onMarkAsRead($event)"
                />
              </a>
            }
          }
        </div>

        <!-- Panel footer -->
        <div class="notification-panel__footer">
          <a class="notification-panel__view-all" routerLink="/notifications" (click)="closePanel()">
            {{ 'common.view' | translate }} {{ 'notifications.title' | translate }}
            <span zagIcon="arrow_forward" class="notification-panel__view-all-icon"></span>
          </a>
        </div>
      </div>
    </ng-template>
  `,
  styles: [
    `
      :host {
        display: contents;
      }

      .notification-bell {
        position: relative;
        display: inline-flex;
      }

      .notification-bell__button {
        position: relative;
      }

      .notification-bell__button--active {
        background: var(--surface-color-hover);
        color: var(--color-primary);
      }

      .notification-bell__icon {
        font-size: 22px;
      }

      .notification-bell__badge {
        position: absolute;
        top: 3px;
        right: 3px;
        min-width: 16px;
        height: 16px;
        padding: 0 3px;
        background: var(--color-error);
        color: #fff;
        font-size: 9px;
        font-weight: 700;
        line-height: 16px;
        text-align: center;
        border-radius: 8px;
        border: 2px solid var(--background-color-primary, #fff);
        box-sizing: border-box;
        pointer-events: none;
      }

      /* Panel styles */
      .notification-panel {
        background: var(--background-color-elevated);
        border: 1px solid var(--border-color-default);
        border-radius: var(--border-radius-lg);
        box-shadow: var(--shadow-lg);
        overflow: hidden;
        display: flex;
        flex-direction: column;
        max-height: min(480px, calc(100vh - 80px));
      }

      .notification-panel__header {
        display: flex;
        align-items: center;
        gap: var(--spacing-sm);
        padding: var(--spacing-sm) var(--spacing-md);
        border-bottom: 1px solid var(--border-color-default);
        flex-shrink: 0;
      }

      .notification-panel__title {
        font-size: var(--font-size-caption);
        font-weight: 700;
        color: var(--text-color-primary);
        margin: 0;
        flex: 1;
      }

      .notification-panel__count {
        font-size: 11px;
        color: var(--text-color-secondary);
        white-space: nowrap;
      }

      .notification-panel__mark-all-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
        border: none;
        background: transparent;
        border-radius: var(--border-radius-sm);
        color: var(--text-color-secondary);
        cursor: pointer;
        transition: all var(--transition-duration-fast);
        flex-shrink: 0;

        &:hover {
          background: var(--surface-color-hover);
          color: var(--color-primary);
        }

        span {
          font-size: 18px;
        }
      }

      .notification-panel__list {
        flex: 1;
        overflow-y: auto;
        min-height: 80px;
      }

      .notification-panel__item-link {
        display: block;
        text-decoration: none;
        color: inherit;
        border-bottom: 1px solid var(--border-color-default);

        &:last-child {
          border-bottom: none;
        }
      }

      .notification-panel__empty {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: var(--spacing-xs);
        padding: var(--spacing-xl) var(--spacing-md);
        color: var(--text-color-secondary);
      }

      .notification-panel__empty-icon {
        font-size: 36px;
        opacity: 0.4;
      }

      .notification-panel__empty-text {
        font-size: var(--font-size-caption);
        margin: 0;
      }

      .notification-panel__footer {
        border-top: 1px solid var(--border-color-default);
        flex-shrink: 0;
      }

      .notification-panel__view-all {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: var(--spacing-xs);
        padding: var(--spacing-sm) var(--spacing-md);
        text-decoration: none;
        font-size: var(--font-size-caption);
        font-weight: 500;
        color: var(--color-primary);
        transition: background var(--transition-duration-fast);

        &:hover {
          background: var(--surface-color-hover);
        }
      }

      .notification-panel__view-all-icon {
        font-size: 16px;
      }
    `,
  ],
})
export class NotificationBellComponent implements OnDestroy {
  private readonly notifications = inject(NotificationsService);
  private readonly router = inject(Router);

  readonly unreadCount = this.notifications.unreadCount;
  readonly hasUnread = this.notifications.hasUnread;
  readonly latestNotifications = computed(() => this.notifications.gateway.latestNotifications().slice(0, 10).map(toNotificationResponse));

  readonly panelOpen = signal(false);

  protected readonly bellButton = viewChild<ElementRef<HTMLButtonElement>>('bellButton');

  readonly bellAriaLabel = computed(() => {
    const count = this.unreadCount();
    if (count === 0) return 'notifications.title';
    return `notifications.title (${count})`;
  });

  readonly panelPositions: ConnectedPosition[] = [
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top',
      offsetY: 4,
    },
    // Fallback: open upward if not enough space below
    {
      originX: 'end',
      originY: 'top',
      overlayX: 'end',
      overlayY: 'bottom',
      offsetY: -4,
    },
  ];

  togglePanel(): void {
    this.panelOpen.update(v => !v);
  }

  closePanel(): void {
    this.panelOpen.set(false);
  }

  ngOnDestroy(): void {
    this.closePanel();
  }

  protected onMarkAsRead(id: string): void {
    this.notifications.markAsRead(id).subscribe();
  }

  protected onMarkAllAsRead(): void {
    this.notifications.markAllAsRead().subscribe();
  }

  protected onNotificationClick(notification: NotificationResponse, event: Event): void {
    event.preventDefault();
    this.closePanel();
    void this.router.navigate(['/notifications', notification.id]);
  }
}
